package com.advaita.cloud.drive.ui.store

import com.advaita.cloud.drive.shared.CuratedInputFormatWire
import com.advaita.cloud.drive.shared.CuratedInputPicks
import com.advaita.cloud.drive.shared.CuratedPickDto
import com.advaita.cloud.drive.shared.CuratedRunRequest
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind

/**
 * ADV-669 session E — the file/folder pickers for a curated workflow's file inputs.
 *
 * Pure data + logic (no Compose, no network) so it's unit-testable under jsTest/Node,
 * mirroring [PullCredentialsUi]'s split of "policy" from "widget". The Compose side
 * lives in `components/CuratedRunModal.kt`.
 *
 * ## D21 — the picker enumerates, DataHub never expands a folder
 *
 * A picked folder is walked HERE, through the browse API the file table already uses
 * ([com.advaita.cloud.drive.ui.api.FilesApi.listFiles]), under the scientist's own
 * session — see [walkFolder]. The result is an explicit `{file_id, rel}` list, matching
 * the backend's wire contract (`ResolveInputsWire.PickBody`, session A,
 * `feature/ADV-669-sA-resolve-picks`). Nothing here calls a "resolve" or "expand"
 * endpoint — there isn't one, by design.
 *
 * There is deliberately no shared DTO for [CuratedInputSpec] yet: no endpoint supplies
 * one (that lands with session B2 — the curated submit path). These types are the
 * picker's own model, shaped to match the wire contract's field names so B2 is a
 * drop-in wire-up rather than a rename.
 */

/** How many files a curated input takes. Mirrors the backend's
 *  `CuratedInputFormat` (session A, `CuratedInputs.kt`) — kept as an independent
 *  copy because there is no shared endpoint for a curated document's declared
 *  inputs yet. */
enum class CuratedInputFormat { FILE_PATH, FILE_SET }

/**
 * The two steps of the OPC run wizard (ADV-669 S1): [SELECT] is the card list of
 * opinionated use-cases; [PICK] is the file/folder picker for the chosen one — today's
 * picker content, unchanged.
 */
enum class OpcWizardStep { SELECT, PICK }

/**
 * The wizard's whole state, as **immutable pure data** — no `mutableStateOf`, no Compose
 * imports, so the step machine is a Node unit test rather than a browser one (the same
 * split this file's header documents for the rest of the picker). The Compose holder in
 * `Modal.kt` owns exactly one of these and swaps it on each transition, so there is one
 * copy of the rules and not two.
 */
data class OpcWizardState(
    val step: OpcWizardStep = OpcWizardStep.SELECT,
    val chosen: CuratedWorkflowSpec? = null,
    val picksByInput: Map<String, List<CuratedPick>> = emptyMap(),
)

/**
 * The state a freshly-opened wizard starts in for [catalog].
 *
 * A catalog with exactly ONE entry pre-selects it, so a future single-OPC deployment
 * doesn't open on a dead Next button. With two or more, nothing is pre-selected: the
 * scientist must make the choice, which is the entire point of an *opinionated*
 * use-case.
 */
fun initialOpcWizardState(catalog: List<CuratedWorkflowSpec>): OpcWizardState =
    catalog.singleOrNull()?.let { OpcWizardState().choose(it) } ?: OpcWizardState()

/** Whether step 1's Next may fire — i.e. an OPC has been chosen. */
fun canAdvance(chosen: CuratedWorkflowSpec?): Boolean = chosen != null

/** Whether step 2's Run may fire. Delegates the arity rules to [formIsComplete] — the
 *  picker already owns them and a second copy would drift. */
fun canRun(chosen: CuratedWorkflowSpec?, picksByInput: Map<String, List<CuratedPick>>): Boolean =
    chosen != null && formIsComplete(chosen.inputs, picksByInput)

/**
 * The picks map to carry into [next] when the scientist picks an OPC on step 1.
 *
 * Re-choosing the SAME OPC keeps everything: Back is documented as preserving picks, and
 * clicking the already-selected card is not a change of mind. Choosing a genuinely
 * DIFFERENT one resets to empty lists keyed by the new OPC's own input names — carrying
 * them over would either drop picks silently (the new OPC names its inputs differently)
 * or keep a stale key that nothing on step 2 renders and nothing on submit sends.
 */
fun picksForOpcSwitch(
    previous: CuratedWorkflowSpec?,
    next: CuratedWorkflowSpec,
    current: Map<String, List<CuratedPick>>,
): Map<String, List<CuratedPick>> =
    if (previous?.jobType == next.jobType) current else next.inputs.associate { it.name to emptyList() }

/** Step 1: choose (or re-choose) an OPC. Stays on [OpcWizardStep.SELECT] — Next advances. */
fun OpcWizardState.choose(opc: CuratedWorkflowSpec): OpcWizardState =
    copy(chosen = opc, picksByInput = picksForOpcSwitch(chosen, opc, picksByInput))

/** Step 1's Next. A no-op while nothing is chosen, so a stray call can't strand the
 *  wizard on [OpcWizardStep.PICK] with no OPC to pick against. */
fun OpcWizardState.advance(): OpcWizardState =
    if (!canAdvance(chosen)) this else copy(step = OpcWizardStep.PICK)

/** Step 2's "◂ Back". Keeps [chosen] and [picksByInput] — see [picksForOpcSwitch]. */
fun OpcWizardState.back(): OpcWizardState = copy(step = OpcWizardStep.SELECT)

/** Replace the picks for the chosen OPC (what step 2's picker produces). */
fun OpcWizardState.withPicks(picks: Map<String, List<CuratedPick>>): OpcWizardState =
    copy(picksByInput = picks)

/**
 * What the wizard hands back when the scientist presses Run: **which** OPC they chose
 * plus their picks. The chosen OPC is part of the result because its `jobType` is the
 * routing key the submit body needs — a picks map alone can no longer say what to run
 * now that there is more than one OPC.
 */
data class OpcRunSelection(
    val opc: CuratedWorkflowSpec,
    val picksByInput: Map<String, List<CuratedPick>>,
    /**
     * The scalar knob values the scientist set, keyed by knob name. Empty for an OPC
     * that declares none — which was every OPC until the rnaseq OUC's `reference`.
     */
    val knobValues: Map<String, String> = emptyMap(),
)

/**
 * One file input a curated workflow declares (D4): its name, arity, the curator's
 * declared target, and the human description shown to the scientist picking files
 * against it.
 *
 * [pattern]/[acceptedExtensions] are the file-type constraint used to pre-select
 * obvious candidates from the folder the picker opened in (reviewer feedback on the
 * live rig — see [matchesFileType]). [pattern] mirrors the curated document's JSON
 * Schema `pattern` when the curator supplied one (the agreed-shape examples use
 * `^\S+\.csv$` and `^\S+\.(tar\.gz|tgz|tar)$`). It is NOT always present — the rig
 * template drops it, and there's an open question about `file-set` and JSON Schema
 * `pattern` — so [acceptedExtensions] is the fallback: the "accepted-extensions list"
 * the agreed-shape doc's closing paragraph floats as `file-path`'s nf-core-convention
 * sibling to `pattern`. Neither is required; a spec with neither simply never
 * auto-selects anything (degrades to the pre-existing manual-pick experience, never
 * an error or a broken picker).
 */
data class CuratedInputSpec(
    val name: String,
    val format: CuratedInputFormat,
    /** The curator's declared target — the exact file for FILE_PATH, the target root for FILE_SET. */
    val targetPath: String,
    val description: String,
    val required: Boolean = true,
    val pattern: Regex? = null,
    val acceptedExtensions: List<String> = emptyList(),
)

/**
 * A curated document's identity, carried alongside its [inputs] so the run modal can
 * show the scientist what they're about to run (reviewer feedback: the modal opened
 * titled just "Run workflow" with no indication of which pipeline). [title]/[description]
 * mirror the curated document's own `title`/`description` (agreed-shape's two worked
 * examples, e.g. "Bulk RNA-seq (STAR + Salmon)" / "Alignment with STAR, quantification
 * with Salmon…") — distinct from a per-input [CuratedInputSpec.description], which is
 * unchanged.
 *
 * [jobType] has **no default** on purpose: a blank `jobType`
 * would route a run at nothing, and a missing provenance line is a trust problem, so
 * both must be a compile-time nuisance for whoever authors the next OPC rather than a
 * runtime surprise for the scientist who launches it.
 */
data class CuratedWorkflowSpec(
    val title: String,
    val description: String,
    /**
     * The wisp `job_type`. Per **D11** the registration `id` **is** the `job_type` —
     * no `.drive` suffix is appended (that suffix belongs to the three legacy built-in
     * stages and `PipelineProperties.wispJobTypeSuffix`). This value is sent verbatim
     * as `CuratedRunRequest.jobType`.
     */
    val jobType: String,
    val inputs: List<CuratedInputSpec>,
    /** The curator's scalar knobs, in declaration order. Empty for an OPC with none. */
    val knobs: List<CuratedKnobSpec> = emptyList(),
)

/**
 * One scalar knob the scientist sets by typing or choosing. [choices] non-empty means a
 * closed set and therefore a select; empty means free text.
 *
 * [initial] is the curator's declared default, and it is the control's starting value
 * rather than something sent silently — see [CuratedKnobDto.default] for why that
 * distinction is load-bearing (nothing downstream applies a schema default, so a knob
 * that is never rendered is never sent at all).
 */
data class CuratedKnobSpec(
    val name: String,
    /** The curator's one-line `description` — the control's label prose. */
    val description: String,
    /**
     * The curator's longer `help_text`, rendered as its OWN line beneath [description]
     * rather than appended to it. See `CuratedKnobDto.toSpec` for why: appending assumed
     * punctuation the curator never promised.
     */
    val helpText: String? = null,
    val required: Boolean,
    val initial: String,
    val choices: List<String>,
    /**
     * `format: reference` — the value is the NAME of a wisp reference set, which
     * `classify` expands into the run's params. Worth marking in the model because it
     * changes what the control means: one pick standing for a whole bundle of members,
     * which is what makes pairing a genome with a mismatched index unrepresentable.
     */
    val isReference: Boolean = false,
)

/**
 * One file the scientist picked for one input, already resolved to the wire shape
 * (`CuratedPickDto`: `fileId` + `rel`). [rel] is always populated here (never blank) so
 * the on-screen summary shows a real path, not a placeholder.
 *
 * [displayName]/[sizeBytes] are UI-only — never part of the wire payload (see
 * [buildCuratedRunRequest]) — kept alongside so the summary/deselect list can render
 * without a second lookup.
 */
data class CuratedPick(
    val fileId: String,
    val rel: String,
    val displayName: String,
    val sizeBytes: Long?,
)

/** [CuratedPick.rel] for a file picked on its own — scattered, from any directory:
 *  its basename. Mirrors `CuratedInputResolver.targetFor`'s FILE_SET default
 *  (`member.rel.ifBlank { member.row.originalFileName }`). */
fun scatteredPickRel(file: FileDto): String = file.displayName

/**
 * A file's own full Drive path, given the parent path ([FileDto.logicalPath] for a
 * FILE row) and its name. Mirrors `CuratedInputResolver.sourcePathOf`.
 */
fun ownPath(parentPath: String, name: String): String =
    if (parentPath == "/") "/$name" else "$parentPath/$name"

/**
 * [CuratedPick.rel] for a file found while walking a picked folder's subtree: the
 * file's path relative to the picked folder, subtree preserved, the folder's own name
 * NOT included. Mirrors `CuratedInputResolver.membersOf`
 * (`sourcePathOf(it).removePrefix("$folderPath/")`).
 */
fun folderMemberRel(folderOwnPath: String, fileParentPath: String, fileName: String): String {
    val full = ownPath(fileParentPath, fileName)
    val prefix = folderOwnPath.trimEnd('/') + "/"
    return full.removePrefix(prefix)
}

/**
 * The outcome of one [walkFolder] call: the picks that satisfied [spec]'s declared
 * file-type constraint, plus the arithmetic needed to say what was dropped (reviewer
 * feedback: "the scan-review screen must say what it dropped — silently discarding
 * files a scientist believes they picked is worse than not filtering at all").
 *
 * [totalFiles] counts every leaf file the walk visited, matching or not; `picks.size`
 * is how many satisfied [spec]; `totalFiles - picks.size` is the skipped count. When
 * [spec] carries no file-type guidance at all (no `pattern`, no `acceptedExtensions`),
 * [matchesFileTypeOrUnconstrained] admits everything, so `totalFiles == picks.size` and
 * there is nothing to report — the common case (the rig template drops `pattern`) must
 * never be treated as "23 skipped".
 */
data class FolderWalkResult(val picks: List<CuratedPick>, val totalFiles: Int)

/**
 * Walk a picked folder's subtree via the same per-directory listing the file table
 * already uses (D21: the picker enumerates, under the scientist's own session — no
 * server-side expansion). [listChildren] is `FilesApi::listFiles` in production and a
 * fake in-memory tree in tests.
 *
 * FOLDER rows are recursed into, never emitted as picks. Leaf files are filtered by
 * [matchesFileTypeOrUnconstrained] against [spec] — the SAME predicate the browse view
 * uses to decide what to dim, so a folder pick can never include a file the browse view
 * would have shown de-emphasised, and vice versa. See [FolderWalkResult] for the
 * skipped-count arithmetic this enables.
 */
suspend fun walkFolder(
    folderOwnPath: String,
    spec: CuratedInputSpec,
    listChildren: suspend (path: String) -> List<FileDto>,
): FolderWalkResult {
    val picks = mutableListOf<CuratedPick>()
    var totalFiles = 0
    suspend fun walk(path: String) {
        for (child in listChildren(path)) {
            if (child.kind == FileKind.FOLDER) {
                walk(child.logicalPath)
            } else {
                totalFiles++
                if (matchesFileTypeOrUnconstrained(spec, child.displayName)) {
                    picks += CuratedPick(
                        fileId = child.id,
                        rel = folderMemberRel(folderOwnPath, child.logicalPath, child.displayName),
                        displayName = child.displayName,
                        sizeBytes = child.sizeBytes,
                    )
                }
            }
        }
    }
    walk(folderOwnPath)
    return FolderWalkResult(picks, totalFiles)
}

/**
 * ADV-1330 (the shared-folder create slice, scope 9): where the wizard opened, and so how it
 * browses and walks.
 *
 * - [Own]: My Drive at [Own.path]. Browse and walk by `?path=`, as before.
 * - [Shared]: a folder in Shared with me. [Shared.trail] is the shared root (`trail[0]`) down to
 *   the open folder. Browse by `?folderId=` ([com.advaita.cloud.drive.ui.api.FilesApi.listFolderById])
 *   and walk by the recursive listing ([walkSharedFolder]), never above the shared root, because
 *   `?path=` is always the caller's own namespace.
 *
 * Example: Lena opens Run workflow in Run 42/reads. The wizard opens on `Shared([Run 42, reads])`,
 * and its breadcrumb starts at Run 42.
 */
sealed interface PickerSource {
    data class Own(val path: String) : PickerSource
    data class Shared(val trail: List<FileDto>) : PickerSource

    /** The open folder's name, for the "N files auto-selected from <folder>" note. */
    val folderName: String
        get() = when (this) {
            is Own -> folderDisplayName(path)
            is Shared -> trail.last().displayName
        }
}

/**
 * The shared picker's trail at breadcrumb [depth] (1 is the shared root). It never goes above the
 * root: the roots list mixes owners, and a run takes one owner's files only.
 */
fun sharedPickerTrail(trail: List<FileDto>, depth: Int): List<FileDto> = trail.take(depth.coerceIn(1, trail.size))

/**
 * [walkFolder] for a folder in Shared with me: ONE recursive read by id ([listSubtree] is
 * `FilesApi::listSubtreeById` in production), since `?path=` would read the caller's own drive.
 * Each row keeps its owner's `logicalPath`, so [folderMemberRel] gives the path under [folder],
 * as it does in My Drive. The same filter and the same arithmetic as [walkFolder] apply.
 */
suspend fun walkSharedFolder(
    folder: FileDto,
    spec: CuratedInputSpec,
    listSubtree: suspend (folderId: String) -> List<FileDto>,
): FolderWalkResult {
    val files = listSubtree(folder.id).filter { it.kind == FileKind.FILE }
    val picks = files.filter { matchesFileTypeOrUnconstrained(spec, it.displayName) }.map {
        CuratedPick(
            fileId = it.id,
            rel = folderMemberRel(folder.logicalPath, it.logicalPath, it.displayName),
            displayName = it.displayName,
            sizeBytes = it.sizeBytes,
        )
    }
    return FolderWalkResult(picks, files.size)
}

/**
 * ADV-1330 (scope 9): what the wizard tells a person who runs on shared files. It is the
 * backend's rule: the outputs go into a new `<prefix>-<runId>` folder beside the anchor file (the
 * first pick of the first `file-path` input, else the first pick of the first input with picks),
 * and `upload` is checked on the anchor's folder.
 */
const val SHARED_RUN_OUTPUT_NOTE =
    "The results go into a new folder next to the main input file. The main input file is the file for the " +
        "first single-file input that has files picked. If there is no such input, it is the first file of the " +
        "first input that has files picked."

/**
 * Whether [fileName] satisfies [spec]'s declared file-type constraint: [CuratedInputSpec.pattern]
 * when present, else [CuratedInputSpec.acceptedExtensions]. Neither present means no
 * candidate ever matches — a safe default (nothing pre-selected, not an error) rather
 * than guessing.
 *
 * [pattern] is matched against the WHOLE name (`Regex.matches`, not `find`) — the
 * agreed-shape patterns are already anchored (`^...$`) and JSON Schema's own `pattern`
 * semantics are a full-string test. [acceptedExtensions] is a plain case-insensitive
 * suffix check.
 */
fun matchesFileType(spec: CuratedInputSpec, fileName: String): Boolean {
    val pattern = spec.pattern
    return when {
        pattern != null -> pattern.matches(fileName)
        spec.acceptedExtensions.isNotEmpty() -> spec.acceptedExtensions.any { fileName.endsWith(it, ignoreCase = true) }
        else -> false
    }
}

/**
 * Whether [spec] declares any file-type constraint at all — a [CuratedInputSpec.pattern]
 * or a non-empty [CuratedInputSpec.acceptedExtensions]. [matchesFileType] already answers
 * `false` for every name when neither is present (the right default for auto-selection: no
 * guidance means no guess), but dimming and folder-filtering need the OPPOSITE default —
 * no guidance must mean "treat everything as matching" (nothing dimmed, nothing filtered),
 * per the reviewer feedback that absence of curator guidance must never degrade the
 * picker. This is that gate; [matchesFileTypeOrUnconstrained] is what combines it with
 * [matchesFileType] for those two call sites.
 */
fun hasFileTypeGuidance(spec: CuratedInputSpec): Boolean =
    spec.pattern != null || spec.acceptedExtensions.isNotEmpty()

/**
 * The single predicate behind BOTH visual dimming (browse view) and folder-walk
 * filtering ([walkFolder]) — reviewer feedback explicitly warns against a second,
 * drifting predicate for either. True when [spec] has no file-type guidance at all
 * ([hasFileTypeGuidance] false — everything counts as a match, so nothing is dimmed or
 * dropped), otherwise delegates to [matchesFileType].
 */
fun matchesFileTypeOrUnconstrained(spec: CuratedInputSpec, fileName: String): Boolean =
    !hasFileTypeGuidance(spec) || matchesFileType(spec, fileName)

/**
 * Human phrasing of [spec]'s declared file-type constraint, for messages that name it
 * (the mismatch caution below, and the browse view's "none of these files matched…").
 * Only meaningful when [hasFileTypeGuidance] is true; callers check that first.
 */
fun describeFileTypeGuidance(spec: CuratedInputSpec): String =
    spec.pattern?.let { "pattern (`${it.pattern}`)" }
        ?: "file type (${spec.acceptedExtensions.joinToString(", ")})"

/**
 * Picks already in [picks] that do NOT satisfy [spec]'s declared file-type constraint —
 * i.e. files the scientist picked while they were dimmed in the browse view, or added
 * from a folder walk before a curator narrowed the pattern. Empty when [spec] carries no
 * file-type guidance at all (nothing to warn about — see [hasFileTypeGuidance]).
 *
 * This is informational bookkeeping only: nothing here removes or blocks a pick. A
 * curator's `pattern` can be wrong, absent, or narrower than reality, and the scientist's
 * pick is the one that runs regardless — see [mismatchCaption], which is what surfaces
 * this to the screen.
 */
fun mismatchedPicks(spec: CuratedInputSpec, picks: List<CuratedPick>): List<CuratedPick> =
    if (!hasFileTypeGuidance(spec)) emptyList() else picks.filterNot { matchesFileType(spec, it.displayName) }

/**
 * The quiet, non-blocking caution shown on the overview when one or more of [spec]'s
 * current [picks] don't match its declared file-type constraint — e.g. "1 selected file
 * doesn't match the expected pattern (`^\S+\.csv$`)". Null when there is nothing to say
 * (every pick matches, or [spec] carries no guidance at all). Purely informational: the
 * Run button never gates on this (see [formIsComplete], which this function has no part
 * in) — the scientist's pick is their call, per the reviewer feedback that a hard block
 * would make a legitimate run impossible with no way out.
 */
fun mismatchCaption(spec: CuratedInputSpec, picks: List<CuratedPick>): String? {
    val mismatched = mismatchedPicks(spec, picks)
    if (mismatched.isEmpty()) return null
    val plural = mismatched.size != 1
    val subject = if (plural) "${mismatched.size} selected files" else "1 selected file"
    val verb = if (plural) "don't" else "doesn't"
    return "$subject $verb match the expected ${describeFileTypeGuidance(spec)}"
}

/**
 * Files in [folderFiles] (one directory's already-fetched listing — the folder the
 * picker opened in) that satisfy [spec]'s declared type. FOLDER rows are never
 * candidates — a folder is picked explicitly via "Use this folder" (D21's walk), not
 * swept up by auto-selection.
 */
fun matchingCandidates(spec: CuratedInputSpec, folderFiles: List<FileDto>): List<FileDto> =
    folderFiles.filter { it.kind == FileKind.FILE && matchesFileType(spec, it.displayName) }

/**
 * The pick set to pre-select for [spec] from the folder the picker opened in
 * (reviewer feedback: pre-select the obvious samplesheet/FASTQ files rather than
 * opening on an empty picker).
 *
 * FILE_PATH auto-selects ONLY when the match is unambiguous — exactly one candidate.
 * Two or more candidates means the scientist must choose: guessing "the first one"
 * risks silently running the wrong samplesheet, so ambiguity selects NOTHING rather
 * than picking one.
 *
 * FILE_SET auto-selects every candidate — an over-inclusive reads set is safe (it's
 * reviewed, and any file can be deselected before Run), unlike an under- or
 * wrongly-inclusive single samplesheet.
 */
fun autoSelectCandidates(spec: CuratedInputSpec, folderFiles: List<FileDto>): List<FileDto> {
    val candidates = matchingCandidates(spec, folderFiles)
    return when (spec.format) {
        CuratedInputFormat.FILE_PATH -> if (candidates.size == 1) candidates else emptyList()
        CuratedInputFormat.FILE_SET -> candidates
    }
}

/** One auto-selection pass over every declared input, from a single folder's listing. */
data class AutoSelection(
    val picksByInput: Map<String, List<CuratedPick>>,
    /** Total files pre-selected across every input — zero means "say nothing" (the
     *  picker degrades to the plain manual-pick experience, not a broken or empty one). */
    val totalSelected: Int,
)

/**
 * Run [autoSelectCandidates] for every declared input against one folder's listing,
 * turning matches into [CuratedPick]s via [scatteredPickRel] (these are direct
 * children of the folder the modal opened in, not a folder walk, so the scattered
 * — basename-only — `rel` is correct).
 */
fun autoSelectAll(specs: List<CuratedInputSpec>, folderFiles: List<FileDto>): AutoSelection {
    val picks = mutableMapOf<String, List<CuratedPick>>()
    var total = 0
    specs.forEach { spec ->
        val chosen = autoSelectCandidates(spec, folderFiles)
        if (chosen.isNotEmpty()) {
            picks[spec.name] = chosen.map { CuratedPick(it.id, scatteredPickRel(it), it.displayName, it.sizeBytes) }
            total += chosen.size
        }
    }
    return AutoSelection(picks, total)
}

/** The folder name to name-drop in the auto-selection note ("N files auto-selected
 *  from <this>") — the path's last segment, or a friendly label for the root. */
fun folderDisplayName(path: String): String =
    path.trimEnd('/').substringAfterLast('/').ifBlank { "the root folder" }

/** Sum of every pick's size; unsized picks (shouldn't normally happen — Drive stamps
 *  size at commit) count as zero rather than making the whole total unknown. */
fun totalPickedBytes(picks: List<CuratedPick>): Long = picks.sumOf { it.sizeBytes ?: 0L }

/** Human-readable byte count for the picker's summary line ("247 files, 12.4 GB"). */
fun formatPickedSize(bytes: Long): String = when {
    bytes >= 1_073_741_824L -> "${roundOneDecimal(bytes / 1_073_741_824.0)} GB"
    bytes >= 1_048_576L -> "${roundOneDecimal(bytes / 1_048_576.0)} MB"
    bytes >= 1_024L -> "${roundOneDecimal(bytes / 1_024.0)} KB"
    else -> "$bytes B"
}

private fun roundOneDecimal(value: Double): String {
    val rounded = kotlin.math.round(value * 10.0) / 10.0
    val intPart = rounded.toLong()
    val fracPart = kotlin.math.round((rounded - intPart) * 10).toLong()
    return "$intPart.$fracPart"
}

/** Result of merging a batch of new picks into an input's existing list. */
data class PickMergeResult(
    val merged: List<CuratedPick>,
    /** `rel`s that collided with a DIFFERENT already-picked file — the batch was
     *  rejected as a whole (see [mergePicks]) when this is non-empty. */
    val collidedRels: List<String>,
)

/**
 * Add [additions] to [existing] for one input's picks.
 *
 * A file already present (same `fileId`) is silently skipped — re-picking the same
 * file or re-walking the same folder is a no-op, not a duplicate. A NEW file whose
 * `rel` collides with a DIFFERENT file already picked is the cross-directory basename
 * clash the backend resolver also rejects (`TARGET_COLLISION`) — surfaced here early,
 * before an hour-long run, per D21's rationale. All-or-nothing: if any collision is
 * found, the WHOLE batch is rejected (nothing added) so a folder walk never lands
 * partially and the summary the scientist reviews is never silently incomplete.
 */
fun mergePicks(existing: List<CuratedPick>, additions: List<CuratedPick>): PickMergeResult {
    val existingFileIds = existing.mapTo(mutableSetOf()) { it.fileId }
    val relOwner = existing.associate { it.rel to it.fileId }
    val collided = mutableListOf<String>()
    val toAdd = mutableListOf<CuratedPick>()
    val seenFileIds = existingFileIds.toMutableSet()
    for (addition in additions) {
        if (addition.fileId in seenFileIds) continue
        val owner = relOwner[addition.rel]
        if (owner != null && owner != addition.fileId) {
            collided += addition.rel
            continue
        }
        seenFileIds += addition.fileId
        toAdd += addition
    }
    if (collided.isNotEmpty()) return PickMergeResult(existing, collided)
    return PickMergeResult(existing + toAdd, emptyList())
}

/** Remove one pick by file id — the deselect action on the summary list. */
fun removePick(existing: List<CuratedPick>, fileId: String): List<CuratedPick> =
    existing.filterNot { it.fileId == fileId }

/**
 * Whether [spec] has a usable set of picks: FILE_PATH takes exactly one (or none, if
 * optional); FILE_SET takes one or more (or none, if optional). Mirrors the backend
 * resolver's `MISSING_REQUIRED_INPUT`/`WRONG_ARITY` gates, applied early so the Run
 * button doesn't enable on an input that would only fail at resolve time.
 */
fun isInputSatisfied(spec: CuratedInputSpec, picks: List<CuratedPick>): Boolean = when (spec.format) {
    CuratedInputFormat.FILE_PATH -> picks.size == 1 || (picks.isEmpty() && !spec.required)
    CuratedInputFormat.FILE_SET -> picks.isNotEmpty() || !spec.required
}

/** True once every declared input has a usable pick set — gates the Run button. */
fun formIsComplete(specs: List<CuratedInputSpec>, picksByInput: Map<String, List<CuratedPick>>): Boolean =
    specs.all { isInputSatisfied(it, picksByInput[it.name].orEmpty()) }

private fun CuratedInputFormat.wireName(): String = when (this) {
    CuratedInputFormat.FILE_PATH -> CuratedInputFormatWire.FILE_PATH
    CuratedInputFormat.FILE_SET -> CuratedInputFormatWire.FILE_SET
}

/**
 * Build the body for `POST /api/pipeline/curated/run` from the chosen OPC and the
 * scientist's picks. Only inputs with at least one pick are included — an optional input
 * the scientist skipped contributes nothing, matching the backend resolver treating an
 * empty optional input as zero files rather than a rejection.
 *
 * `params` carries the scientist's scalar knobs. A knob left blank is OMITTED rather
 * than sent as an empty string: an empty value is not "the default" to anything
 * downstream — wisp's `hydrate` passes the knobs through verbatim and nothing applies a
 * schema default — so sending `""` would hand the pipeline a blank where it expected a
 * path or a name. Absent is the honest wire form for "not set".
 *
 * `outdir` is never included: it is platform-owned (D17) and `CuratedSubmit.render`
 * REJECTS a submitter-supplied one, so sending it would turn every run into a 400. The
 * catalog projection already declines to render a control for it.
 *
 * The declared `format` travels with each input because it is what lets the server pick
 * the run's anchor file (`CuratedSubmit.anchorInputName`); it is not part of the wisp
 * submit body.
 */
fun buildCuratedRunRequest(
    opc: CuratedWorkflowSpec,
    picksByInput: Map<String, List<CuratedPick>>,
    knobValues: Map<String, String> = emptyMap(),
): CuratedRunRequest = CuratedRunRequest(
    // D11: the registration `id` IS the job_type — sent verbatim, no `.drive` suffix.
    jobType = opc.jobType,
    // Declaration order, and only the knobs this OPC declares — a stale value left over
    // from a different OPC the scientist looked at first must not ride along.
    params = opc.knobs.mapNotNull { knob ->
        knobValues[knob.name]?.trim()?.takeIf { it.isNotEmpty() }?.let { knob.name to it }
    }.toMap(),
    inputs = opc.inputs.mapNotNull { spec ->
        val picks = picksByInput[spec.name].orEmpty()
        if (picks.isEmpty()) return@mapNotNull null
        CuratedInputPicks(
            name = spec.name,
            picks = picks.map { CuratedPickDto(it.fileId, it.rel) },
            format = spec.format.wireName(),
        )
    },
)
